<ng-container *ngIf="collapsible; else nonCollapsibleTemplate">
  <p-accordion [(activeIndex)]="accordionActiveIndex">
    <p-accordionTab>
      <ng-template pTemplate="header">
        <div class="flex align-items-center justify-content-between w-full pr-2">
          <div class="flex align-items-center gap-3">
            <i class="pi pi-user text-primary text-xl"></i>
            <div class="flex flex-column">
              <span class="font-semibold text-lg mb-2">
                <ng-container *ngIf="form.get('firstName')?.value || form.get('lastName')?.value; else noNameTemplate">
                  {{ form.get('title')?.value }} {{ form.get('firstName')?.value }} {{ form.get('lastName')?.value }}
                </ng-container>
                <ng-template #noNameTemplate>
                  <span class="text-500">{{ crudMode === CrudPatientMode.CREATE ? 'New Patient' : 'Patient Details'
                    }}</span>
                </ng-template>
              </span>
              <div class="flex align-items-center gap-4 text-sm text-600"
                *ngIf="form.get('email')?.value || form.get('dateOfBirth')?.value">
                <span *ngIf="form.get('email')?.value" class="flex align-items-center gap-1">
                  <i class="pi pi-envelope"></i>
                  {{ form.get('email')?.value }}
                </span>
                <span *ngIf="form.get('dateOfBirth')?.value" class="flex align-items-center gap-1">
                  <i class="pi pi-calendar"></i>
                  {{ form.get('dateOfBirth')?.value | date:'mediumDate' }}
                  <span *ngIf="getPatientAge()" class="text-500">({{ getPatientAge() }} years old)</span>
                </span>
                <span *ngIf="getSelectedSexName()" class="flex align-items-center gap-1">
                  <i class="pi pi-users"></i>
                  {{ getSelectedSexName() }}
                </span>
                <span *ngIf="getSelectedEthnicityName()" class="flex align-items-center gap-1">
                  <i class="pi pi-globe"></i>
                  {{ getSelectedEthnicityName() }}
                </span>
              </div>
            </div>
          </div>

        </div>
      </ng-template>

      <ng-container *ngTemplateOutlet="formContentTemplate"></ng-container>

    </p-accordionTab>
  </p-accordion>
</ng-container>

<ng-template #nonCollapsibleTemplate>
  <ng-container *ngTemplateOutlet="formContentTemplate"></ng-container>
</ng-template>

<ng-template #formContentTemplate>
  <form [formGroup]="form" class="p-fluid">
    <div class="grid formgrid mb-3">
      <div class="field col-12">
        <label for="id">Patient ID</label>
        <input pInputText id="id" formControlName="id" class="w-full" [readOnly]="true" />
      </div>
    </div>

    <div class="grid formgrid">
      <div class="field col-3">
        <label for="title" class="block text-900 font-medium mb-2">
          {{ 'TITLE' | translate }}
          <span class="p-error">*</span>
        </label>

        <p-dropdown formControlName="title" id="title" class="w-full" [options]="titles" appendTo="body" />

        <div *ngIf="form.get('title')?.invalid && form.get('title')?.touched" class="text-red-500 mb-2">
          <small>Title is required.</small>
        </div>
      </div>

      <div class="field col-9"></div>

      <div class="field col-6">
        <label for="lastName" class="block text-900 font-medium mb-2">
          {{ 'FIRST_NAME' | translate }}
          <span class="p-error">*</span>
        </label>
        <input pInputText id="firstName" formControlName="firstName" class="w-full" />
        <div *ngIf="form.get('firstName')?.invalid && form.get('firstName')?.touched" class="text-red-500 mb-2">
          <small>{{ 'FIRST_NAME_REQUIRED' | translate }}</small>
        </div>
      </div>

      <div class="field col-6">
        <label for="lastName" class="block text-900 font-medium mb-2">
          {{ 'LAST_NAME' | translate }}
          <span class="p-error">*</span>
        </label>
        <input pInputText id="lastName" formControlName="lastName" class="w-full" />
        <div *ngIf="form.get('lastName')?.invalid && form.get('lastName')?.touched" class="text-red-500 mb-2">
          <small>{{ 'LAST_NAME_REQUIRED' | translate }}</small>
        </div>
      </div>

      <div class="field col-6">
        <label for="email" class="block text-900 font-medium mb-2">
          {{ 'EMAIL' | translate }}
        </label>
        <input pInputText id="email" formControlName="email" class="w-full" />
      </div>

      <div class="field col-6">
        <label for="telephone" class="block text-900 font-medium mb-2">
          Telephone
        </label>
        <app-global-phone-input [formGroup]="form" [phoneCodeControlName]="'telephone_code'"
          [phoneNumberControlName]="'telephone'" class="w-full" />
      </div>

      <div class="field col-6">
        <label for="dateOfBirth" class="block text-900 font-medium mb-2">
          Date of Birth
          <span class="p-error">*</span>
        </label>
        <p-calendar id="dateOfBirth" formControlName="dateOfBirth" class="w-full" [showIcon]="true"
          [maxDate]="calendarOptions.maxDate" [yearRange]="calendarOptions.yearRange" [iconDisplay]="'input'"
          [showButtonBar]="false" placeholder="Select a birthdate" appendTo="body"></p-calendar>
        <div *ngIf="form.get('dateOfBirth')?.invalid && form.get('dateOfBirth')?.touched" class="text-red-500 mb-2">
          <small *ngIf="form.get('dateOfBirth')?.errors?.['required']">
            Date of birth is required.
          </small>
          <small *ngIf="form.get('dateOfBirth')?.errors?.['invalidAge']">
            Date of birth is invalid.
          </small>
        </div>
      </div>

      <div class="field col-6">
        <label for="ethnicity" class="block text-900 font-medium mb-2">
          Ethnicity
          <span class="p-error">*</span>
        </label>

        <p-dropdown formControlName="ethnicity_id" optionLabel="displayName" optionValue="id" id="ethnicity"
          class="w-full" appendTo="body" [options]="ethnicities" />

        <div *ngIf="form.get('ethnicity_id')?.invalid && form.get('ethnicity_id')?.touched" class="text-red-500 mb-2">
          <small>Ethnicity is required.</small>
        </div>
      </div>

      <div class="field col-6">
        <label for="sex" class="block text-900 font-medium mb-2">
          Sex
          <span class="p-error">*</span>
        </label>

        <p-dropdown formControlName="sex_id" optionLabel="displayName" optionValue="id" id="sex" class="w-full"
          appendTo="body" [options]="sexes" />

        <div *ngIf="form.get('sex_id')?.invalid && form.get('sex_id')?.touched" class="text-red-500 mb-2">
          <small>Sex is required.</small>
        </div>
      </div>

      <div class="field col-6">
        <label for="parentalMyopia" class="block text-900 font-medium mb-2">
          Parental Myopia
          <span class="p-error">*</span>
        </label>

        <p-dropdown formControlName="parentalMyopia" id="parentalMyopia" class="w-full" appendTo="body"
          [options]="parentalMyopiaOptions" />

        <div *ngIf="form.get('parentalMyopia')?.invalid && form.get('parentalMyopia')?.touched"
          class="text-red-500 mb-2">
          <small>Parental Myopia is required.</small>
        </div>
      </div>

    </div>
  </form>

  <!-- Consent Section - Different display based on CRUD mode -->
  <div class="flex align-items-center justify-content-center flex-column gap-2 mt-4">
    
    <!-- CREATE mode: No consent button shown, handled in submit -->
    <ng-container *ngIf="crudMode === CrudPatientMode.CREATE">
      <!-- No consent UI in CREATE mode -->
    </ng-container>

    <!-- READ mode: Show consent status only -->
    <ng-container *ngIf="crudMode === CrudPatientMode.READ">
      <div class="flex align-items-center gap-2 w-full">
        <span class="font-medium">Research Consent Status:</span>

        @if (consentGiven) {
        <div class="flex align-items-center gap-2 text-green-700">
          <i class="pi pi-check-circle text-green-500"></i>
          <span>Consent Given</span>
        </div>
      } @else {
        <div class="flex align-items-center gap-2 text-red-700">
          <i class="pi pi-times-circle text-red-500"></i>
          <span>Consent Not Given</span>
        </div>
      }

      </div>
    </ng-container>
  </div>

  <div class="flex align-items-center justify-content-center flex-column gap-2 mt-4"
    *ngIf="crudMode !== CrudPatientMode.READ">
    <button pButton class="p-button-primary w-full"
      [label]="crudMode === CrudPatientMode.UPDATE ? 'Update Patient' : 'Check Consent & Add Patient'" 
      [disabled]="form.invalid"
      (click)="showConsentPopup()"></button>
    
    <!-- Helpful text for CREATE mode -->
    <div *ngIf="crudMode === CrudPatientMode.CREATE" class="text-center text-sm text-600 mt-2">
      <i class="pi pi-info-circle mr-1"></i>
      Patient consent will be checked before adding the patient
    </div>
  </div>

  <!-- Consent Dialog -->
  <p-dialog 
    appendTo="body"
    [(visible)]="showConsentDialog" 
    [modal]="true" 
    [closable]="true" 
    [draggable]="false"
    [style]="{width: '90vw', maxWidth: '800px'}" 
    header="Patient Consent" 
    (onHide)="onConsentDialogClose()">
    @if (showConsentDialog) {
    <app-patient-consent 
      (onConsentSaved)="onFormSubmit($event)">
    </app-patient-consent>
    }
  </p-dialog>
</ng-template>